import MainHeaderComponent from '../../components/main-header/main-header.component';
import { Checkbox, Loading, TextInput } from 'carbon-components-react';
import useSWR, { mutate } from 'swr';
import { Edit } from 'react-feather';
import { useState } from 'react';
import './daily-fee.styles.scss';
import { formatCurrencyV2 } from '../../utils/common.util';
import { EditFeeType } from './FeeTypeForm';
import { useParams } from 'react-router-dom';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/user/user.selectors';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import { useHistory } from 'react-router-dom';

export default function SingleFeeTypePage() {
  const { id } = useParams();

  const currentUser = useSelector(selectCurrentUser);
  const { data: classList, isLoading: isClassing } = useSWR('class/list');

  // fetch current fee type
  const singleFeeTypeUrl = `daily-billing/fees/${id}/summary`;
  const { data: d, isLoading } = useSWR(singleFeeTypeUrl);
  const data = d?.dailyBillingFee ?? {};

  const [isOpen, setIsOpen] = useState(null);

  const [isRemoveClasses, setIsRemoveClasses] = useState(false);
  const [classesToBeRemoved, setClassesToBeRemoved] = useState([]);

  function removeClass(classId) {
    if (isRemoveClasses) {
      setClassesToBeRemoved((prev) =>
        prev.includes(classId)
          ? prev.filter((id) => id !== classId)
          : [...prev, classId],
      );
    }
  }

  const {
    trigger: remove,
    isMutating: isRemoving,
    error: removeError,
  } = useCustomMutation(
    `daily-billing/fees/${id}/remove-class`,
    (url, { arg }) => postMethod(url, arg),
  );

  const [classesToAdd, setClassesToAdd] = useState([]);
  function selectClassesToAdd(cls) {
    if (classesToAdd.some((c) => c.id === cls.id)) {
      setClassesToAdd(classesToAdd.filter((c) => c.id !== cls.id));
    } else {
      setClassesToAdd([...classesToAdd, cls]);
    }
  }

  // removedClassses are classes that are in the classList but not in the data.classAmounts
  const removedClasses = classList
    ? classList.filter(
        (cls) => !data?.classAmounts?.some((c) => c.classId === cls.id),
      )
    : [];

  const {
    trigger: addClasses,
    isMutating: isAdding,
    error: addError,
  } = useCustomMutation(`daily-billing/fees/${id}/add-class`, (url, { arg }) =>
    postMethod(url, arg, currentUser.authToken),
  );

  const history = useHistory();

  return (
    <div>
      <MainHeaderComponent />

      <EditFeeType
        data={data}
        isOpen={isOpen === 'edit'}
        closeModal={() => setIsOpen('')}
      />

      <div className="page-container fee-type-page">
        <div className="mt-20 fade-in" hidden={!isLoading}>
          <Loading small={true} withOverlay={false} description="Loading..." />
        </div>
        {!data.name && (
          <p className="text-14 mt-30 text-grey">
            Fee type does not exist or has been deleted.
          </p>
        )}

        <Breadcrumbs
          items={[
            { label: 'Daily Fees', to: '/daily-fees' },
            { label: 'Fee Types', to: '/daily-fees/types' },
            { label: data?.name, active: true },
          ]}
        />

        <hr className="grey-hr mt-210 mb-10" />

        <div
          className="mt-15 h-center"
          style={{ justifyContent: 'space-between', gap: '12px' }}
        >
          <h1 className="h-20">{data?.name}</h1>
          <div className="h-center" style={{ gap: '12px' }}>
            <button
              className="text-12 text-primary font-bold pointer clean-btn "
              style={{ gap: '8px', display: 'flex', alignItems: 'center' }}
              onClick={() => setIsOpen('edit')}
            >
              <Edit size="16" strokeWidth="2px" />
              <span className="text-14">Edit Fee Type</span>
            </button>
            <div style={{ marginLeft: 'auto' }}>
              <Checkbox
                id={`removeClasses-${data.id}`}
                name={`removeClasses-${data.id}`}
                labelText="Remove Classes"
                checked={isRemoveClasses}
                onChange={(value) => setIsRemoveClasses(value)}
              />
            </div>
          </div>
        </div>
        <div className="mt-10">
          <span className="text-12 text-dark">Base Amount: </span>
          <span className="text-16 font-bold">
            {formatCurrencyV2(data?.amount)}
          </span>
        </div>

        <div className="nm mt-30" hidden={isLoading || data.length === 0}>
          {removeError && (
            <p className="text-14 mt-10 text-danger">{removeError?.message}</p>
          )}

          <p className="mt-20 text-14">
            Click on a class below to add students to fee type
          </p>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {data?.classAmounts?.map((cls) => (
              <div
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer', minWidth: '150px' }}
                key={cls?.id}
                className="fee-card"
                onClick={() =>
                  !isRemoveClasses &&
                  history.push(
                    `/daily-fees/types/${data?.id}/class/${cls?.classId}/add-students`,
                  )
                }
                onKeyDown={(e) => {
                  if (!isRemoveClasses && e.key === 'Enter') {
                    history.push(
                      `/daily-fees/types/${data?.id}/class/${cls?.classId}/add-students`,
                    );
                  }
                }}
              >
                <div style={{ display: 'grid', gap: '16px' }}>
                  <span className="font-bold text-20">
                    {cls?.classDescription}
                  </span>
                  <span>{formatCurrencyV2(cls?.amount)}</span>
                </div>
                {isRemoveClasses && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <Checkbox
                      id={`remove-classId-${cls.classId}`}
                      name={`remove-classId-${cls.classId}`}
                      labelText=""
                      checked={classesToBeRemoved.includes(cls.classId)}
                      onChange={() => removeClass(cls.classId)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10" style={{ display: 'flex' }}>
            {isRemoveClasses && (
              <div style={{ marginLeft: 'auto' }}>
                <CustomButton
                  type="button"
                  style={{ flex: '1' }}
                  classes={`primary font-bold`}
                  onClick={() => {
                    remove(
                      { classIds: classesToBeRemoved },
                      {
                        onSuccess() {
                          mutate(singleFeeTypeUrl);
                          mutate('class/list');
                          setIsRemoveClasses(false);
                          setClassesToBeRemoved([]);
                        },
                      },
                    );
                  }}
                  disabled={
                    isRemoving || isLoading || classesToBeRemoved.length < 1
                  }
                >
                  {isRemoving ? (
                    <div className="d-flex-center">
                      <Loading withOverlay={false} small={true} />
                    </div>
                  ) : (
                    'Remove Classes'
                  )}
                </CustomButton>
              </div>
            )}
          </div>
        </div>

        <div className="mt-20 fade-in" hidden={!isClassing}>
          <Loading small={true} withOverlay={false} description="Loading..." />
        </div>
        {removedClasses?.length > 0 && (
          <div className="mt-30">
            <h2 className="text-16" style={{ fontWeight: 600 }}>
              Removed Classes
            </h2>
            <div className="mt-10">
              <span className="text-14">
                Classes that have not been billed for this fee type.
              </span>
            </div>
            <div
              className="mt-20"
              style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}
            >
              {removedClasses?.map((cls) => (
                <ClassCard
                  key={cls?.id}
                  cls={cls}
                  onCheckChange={(newCls) => selectClassesToAdd(newCls)}
                  checked={classesToAdd.some((c) => c.id === cls.id)}
                />
              ))}
            </div>

            {addError && (
              <p className="text-14 mt-10 text-danger">{addError?.message}</p>
            )}

            <div className="mt-20">
              {classesToAdd.length > 0 && (
                <CustomButton
                  type="button"
                  style={{ flex: '1' }}
                  classes={`primary font-bold`}
                  onClick={() => {
                    addClasses(
                      { classAmounts: classesToAdd },
                      {
                        onSuccess() {
                          mutate(singleFeeTypeUrl);
                          setClassesToAdd([]);
                        },
                      },
                    );
                  }}
                  disabled={
                    isAdding || classesToAdd.some((cls) => cls.amount <= 0)
                  }
                >
                  {isAdding ? (
                    <div className="d-flex-center">
                      <Loading withOverlay={false} small={true} />
                    </div>
                  ) : (
                    'Add Classes'
                  )}
                </CustomButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClassCard({ cls, checked, onCheckChange }) {
  const [amountValue, setAmountValue] = useState(0);
  return (
    <div key={cls?.id} className="fee-card" style={{ minWidth: '150px' }}>
      <div style={{ display: 'grid', gap: '16px' }}>
        <span className="font-bold text-20">{cls?.description}</span>

        <TextInput
          id={`add-amount-id-${cls.id}`}
          name={`add-amount-id-${cls.id}`}
          type="number"
          className="text-input"
          size="sm"
          placeholder=""
          labelText=""
          disabled={checked}
          value={amountValue}
          onChange={(e) => setAmountValue(e.target.value)}
        />
      </div>
      <div style={{ alignSelf: 'flex-start' }}>
        <Checkbox
          id={`add-classId-${cls.id}`}
          name={`add-classId-${cls.id}`}
          labelText=""
          checked={checked}
          onChange={(e) =>
            onCheckChange({ ...cls, classId: cls.id, amount: amountValue })
          }
        />
      </div>
    </div>
  );
}
